import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { TrendingUp, TrendingDown, Minus, RefreshCw, Save, Globe, Activity } from "lucide-react";

interface RunRow {
  id: string;
  ran_at: string;
  passed: number;
  failed: number;
  results: any;
}

interface Trend {
  ran_at: string;
  submitted: number;
  indexed: number;
  indexed_from_search: number;
}

function deltaIcon(curr: number, prev: number) {
  if (curr > prev) return <TrendingUp className="w-3 h-3 text-green-400" />;
  if (curr < prev) return <TrendingDown className="w-3 h-3 text-red-400" />;
  return <Minus className="w-3 h-3 text-muted-foreground" />;
}

export function SeoMonitorWidget() {
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [propertyUrl, setPropertyUrl] = useState("");
  const [sitemapUrl, setSitemapUrl] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);

  async function load() {
    setLoading(true);
    const [{ data: runRows }, { data: settingsRows }] = await Promise.all([
      supabase.from("seo_monitor_runs").select("*").order("ran_at", { ascending: false }).limit(20),
      supabase.from("site_settings").select("key,value").in("key", ["gsc_property_url", "gsc_sitemap_url"]),
    ]);
    setRuns((runRows as RunRow[]) ?? []);
    const map = Object.fromEntries((settingsRows ?? []).map((r: any) => [r.key, r.value]));
    setPropertyUrl(map.gsc_property_url ?? "");
    setSitemapUrl(map.gsc_sitemap_url ?? "");
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function saveSettings() {
    setSavingSettings(true);
    try {
      const rows = [
        { key: "gsc_property_url", value: propertyUrl.trim() },
        { key: "gsc_sitemap_url", value: sitemapUrl.trim() },
      ];
      for (const r of rows) {
        const { error } = await supabase.from("site_settings").upsert(r, { onConflict: "key" });
        if (error) throw error;
      }
      toast({ title: "GSC settings saved", description: "Next monitor run will use the new property." });
    } catch (e: any) {
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    } finally {
      setSavingSettings(false);
    }
  }

  async function triggerNow() {
    setTriggering(true);
    try {
      const { error } = await supabase.functions.invoke("seo-monitor", { body: { trigger: "manual" } });
      if (error) throw error;
      toast({ title: "SEO monitor triggered", description: "Refreshing results…" });
      setTimeout(load, 2500);
    } catch (e: any) {
      toast({ title: "Trigger failed", description: e.message, variant: "destructive" });
    } finally {
      setTriggering(false);
    }
  }

  const trends: Trend[] = runs.map((r) => {
    const s = r.results?.summary?.sitemap ?? {};
    return {
      ran_at: r.ran_at,
      submitted: Number(s.submitted ?? 0),
      indexed: Number(s.indexed ?? 0),
      indexed_from_search: Number(s.indexed_from_search_analytics ?? 0),
    };
  });

  const latest = trends[0];
  const prev = trends[1];

  return (
    <div className="space-y-4">
      {/* Configuration card */}
      <div className="bg-card border border-border rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-primary" />
          <h3 className="font-mono text-sm font-semibold">Google Search Console Configuration</h3>
        </div>
        <p className="text-xs text-muted-foreground">
          Server-side indexed-URL counts use the Google Search Console connector (managed under Connectors).
          Set the verified property URL and sitemap below — credentials are never exposed to the browser.
        </p>
        <div className="grid gap-2">
          <label className="text-xs text-muted-foreground" htmlFor="gsc-property">Property URL</label>
          <input
            id="gsc-property"
            type="url"
            value={propertyUrl}
            onChange={(e) => setPropertyUrl(e.target.value)}
            placeholder="https://jp-dork.space/"
            autoComplete="off"
            className="bg-background border border-border rounded px-2 py-1 text-sm font-mono"
          />
          <label className="text-xs text-muted-foreground" htmlFor="gsc-sitemap">Sitemap URL</label>
          <input
            id="gsc-sitemap"
            type="url"
            value={sitemapUrl}
            onChange={(e) => setSitemapUrl(e.target.value)}
            placeholder="https://jp-dork.space/sitemap.xml"
            autoComplete="off"
            className="bg-background border border-border rounded px-2 py-1 text-sm font-mono"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={saveSettings}
            disabled={savingSettings}
            className="flex items-center gap-1 px-3 py-1.5 text-xs rounded bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            <Save className="w-3 h-3" /> {savingSettings ? "Saving…" : "Save settings"}
          </button>
          <button
            onClick={triggerNow}
            disabled={triggering}
            className="flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-border hover:bg-muted disabled:opacity-50"
          >
            <RefreshCw className={`w-3 h-3 ${triggering ? "animate-spin" : ""}`} /> Run monitor now
          </button>
        </div>
      </div>

      {/* Latest stats */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-4 h-4 text-primary" />
          <h3 className="font-mono text-sm font-semibold">Sitemap Submitted vs Indexed</h3>
        </div>
        {loading ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : !latest ? (
          <p className="text-xs text-muted-foreground">No monitor runs recorded yet.</p>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Submitted (GSC)" value={latest.submitted} delta={prev ? latest.submitted - prev.submitted : 0} icon={prev && deltaIcon(latest.submitted, prev.submitted)} />
            <Stat label="Indexed (GSC)" value={latest.indexed} delta={prev ? latest.indexed - prev.indexed : 0} icon={prev && deltaIcon(latest.indexed, prev.indexed)} />
            <Stat label="Indexed (Search Analytics overlap)" value={latest.indexed_from_search} delta={prev ? latest.indexed_from_search - prev.indexed_from_search : 0} icon={prev && deltaIcon(latest.indexed_from_search, prev.indexed_from_search)} />
          </div>
        )}
      </div>

      {/* Trend table */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="font-mono text-sm font-semibold mb-2">Recent runs (last 20)</h3>
        {trends.length === 0 ? (
          <p className="text-xs text-muted-foreground">No history yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="text-muted-foreground border-b border-border">
                  <th className="text-left py-1 pr-3">When</th>
                  <th className="text-right py-1 pr-3">Submitted</th>
                  <th className="text-right py-1 pr-3">Indexed</th>
                  <th className="text-right py-1 pr-3">Search overlap</th>
                  <th className="text-right py-1 pr-3">Passed</th>
                  <th className="text-right py-1">Failed</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r, i) => {
                  const t = trends[i];
                  return (
                    <tr key={r.id} className="border-b border-border/50">
                      <td className="py-1 pr-3 whitespace-nowrap">{new Date(r.ran_at).toLocaleString()}</td>
                      <td className="py-1 pr-3 text-right">{t.submitted}</td>
                      <td className="py-1 pr-3 text-right">{t.indexed}</td>
                      <td className="py-1 pr-3 text-right">{t.indexed_from_search}</td>
                      <td className="py-1 pr-3 text-right text-green-400">{r.passed}</td>
                      <td className="py-1 text-right text-red-400">{r.failed}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, delta, icon }: { label: string; value: number; delta: number; icon: any }) {
  return (
    <div className="bg-background border border-border rounded p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-mono font-bold mt-1">{value}</div>
      <div className="text-xs flex items-center gap-1 mt-1 text-muted-foreground">
        {icon} {delta > 0 ? `+${delta}` : delta} vs prev run
      </div>
    </div>
  );
}
